/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasource, toNewRow } from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXSetter } from '@cloudio-saas/statex';
import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Theme,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  Chip,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Icon, { parseIconText } from 'icons/Icon';
import {
  activeSidebarPanelAtom,
  DSView,
  sidebarActiveDatasourceIdAtom,
  sidebarDatasourceEditModeAtom,
  sidebarPageViewAtom,
  sidebarPageWizardDataSourceAtom,
  sidebarPageWizardStepAtom,
  sidebarPageWizardTitleAtom,
} from 'layout/Sidebar/atoms';
import { usePending } from 'platform/PlatformHooks';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { memo, useContext, useState } from 'react';
import Store from 'store/Store';
import useDataStore from 'store/useDataStore';
import {
  useIoDatasourceAccessVStore,
  useIoDatasourceAttributesStore,
  useIoDatasourceEditStore,
} from 'store/utils/utils';
import { stopPropagation } from 'utils/events';
import { IconPulse, MenuItemPulse } from 'utils/Pulse';
import { maximizeSidebarWidthAtom } from 'utils/state';
import WaveDots from 'utils/WaveDots';
import { IoPages } from '@cloudio-saas/datasource-types';
import { useStoreRecordsOrder } from 'components/DataStoreReference/DataStoreReference';
import { Dialog } from '@mui/material';
import { Records } from 'store/storeTypes';
import { Platform } from 'componentregistry/platform/Platform';

const useStyles = makeStyles((theme: Theme) => ({
  listIcon: {
    minWidth: 32,
  },
  label: {
    textAlign: 'start',
    paddingRight: theme.spacing(1),
    minWidth: 500,
  },
  icon: {
    marginRight: 8,
  },
}));

interface SyncRecord {
  ds: string;
  changes: string[];
}

export function RefPagesPopup({
  appUid,
  datasourceUid,
  platform,
  recordsOrder,
  records,
  onClose,
}: {
  appUid: string;
  datasourceUid: string;
  platform: Platform;
  recordsOrder: string[];
  records: Records<IoPages>;
  onClose: () => void;
}) {
  const classes = useStyles();
  const isReferred = recordsOrder.length;

  return (
    <Dialog
      onClick={stopPropagation}
      onClose={onClose}
      onMouseDown={stopPropagation}
      open
    >
      <DialogTitle>
        {datasourceUid}
        <IconButton
          data-testid="close"
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <List sx={{ pt: 0 }}>
        {isReferred ? (
          recordsOrder.map((id) => {
            const _rec = records[id];
            const { iconName, iconType } = parseIconText(_rec.pageIcon);
            return (
              <ListItem key={id} sx={{ pt: 0 }}>
                <Icon
                  className={classes.icon}
                  iconName={iconName || 'desktop'}
                  iconType={iconType}
                />
                <ListItemText
                  className={classes.label}
                  primary={_rec.pageName}
                />
                <Icon
                  data-testid={`${_rec.pageUid}-goto-page`}
                  onClick={() => {
                    onClose();
                    platform.goToPage(appUid, _rec.pageUid);
                  }}
                  className={classes.icon}
                  color="primary"
                  iconName="external-link"
                  iconType="light"
                />
              </ListItem>
            );
          })
        ) : (
          <ListItem key="noDataFound">
            <Icon
              className={classes.icon}
              iconName="exclamation-triangle"
              iconType="light"
            />
            <ListItemText
              className={classes.label}
              primary="No referred pages found"
            />
          </ListItem>
        )}
      </List>
    </Dialog>
  );
}

function DataSourceActions({
  className,
  value,
  setView,
  store,
  appUid,
  isViewOnlyUser = false,
}: {
  className: string;
  value: string;
  setView: (_view: DSView) => void;
  store: Store<IoDatasource>;
  appUid: string;
  isViewOnlyUser: boolean;
}): React.ReactElement {
  const classes = useStyles();
  const set = useStateXSetter();
  const platform = useContext(PlatformContext);
  const setEditId = useStateXAtomSetter(sidebarActiveDatasourceIdAtom);
  const setEditMode = useStateXAtomSetter(sidebarDatasourceEditModeAtom);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(
    undefined,
  );
  const open = Boolean(anchorEl);
  const [isPending1, setPending1] = usePending();
  const [isPending2, setPending2] = usePending();
  const [isPending3, setPending3] = usePending();
  const dsEditStore = useIoDatasourceEditStore(appUid);
  const dsAttrStore = useIoDatasourceAttributesStore(appUid);
  const dsAccessStore = useIoDatasourceAccessVStore(appUid);
  const syncStore = useDataStore<SyncRecord>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceSync',
    alias: 'IoDatasourceSyncAlias',
  });

  const refObjStore = useDataStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesDSRefAlias',
  });
  const recordsOrder = useStoreRecordsOrder(refObjStore);
  const refObjSize = recordsOrder.length;
  const refObjRecs = refObjStore.records();
  const [isBusy, setBusy] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const datasourceUid = store.getRecord(value).datasourceUid || '';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setBusy(true);
    store.setCurrentRecordId(value);
    const record = store.getRecord(value);
    refObjStore
      .query({
        props: {
          projection: {
            pageUid: 1,
            pageName: 1,
            pageIcon: 1,
          },
          filter: [
            { appUid: { is: appUid } },
            {
              md: { like: `"dataSource": "${record.datasourceUid}"` },
            },
          ],
        },
      })
      .then(() => {
        setBusy(false);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event?: any) => {
    event?.stopPropagation();
    setAnchorEl(undefined);
  };

  const handleEdit = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    platform.startAsyncTransition(async () => {
      store.setCurrentRecordId(value);
      const record = store.getRecord(value);
      if (record?.datasourceUid) {
        set(maximizeSidebarWidthAtom, 'Y');
        setEditId(record.datasourceUid);
        setAnchorEl(undefined);
        setEditMode('Edit');
        setView('edit');
      }
    }, setPending1);
  };

  const onClose = () => {
    setShowPopup(false);
  };

  return (
    <>
      <IconButton
        data-testid={`${datasourceUid}-attrs`}
        className={className}
        color="primary"
        aria-label={`View Attributes for ${datasourceUid}`}
        data-tip={`View Attributes for ${datasourceUid}`}
        onClick={(e) => {
          e.stopPropagation();
          platform.startAsyncTransition(async () => {
            store.setCurrentRecordId(value);
            const record = store.getRecord(value);
            if (record.datasourceUid) {
              setEditId(record.datasourceUid);
              setView('listChildren');
            }
          }, setPending1);
        }}
        onMouseDown={stopPropagation}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          View Attributes for {datasourceUid}
        </span>
        {isPending1 ? <WaveDots active /> : <Icon iconName="list" />}
      </IconButton>
      <IconButton
        data-testid={`${datasourceUid}-edit`}
        className={className}
        color="primary"
        aria-label={`Edit ${datasourceUid}`}
        onClick={handleEdit}
        onMouseDown={stopPropagation}
        data-tip={`Edit ${datasourceUid}`}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {`Edit ${datasourceUid}`}
        </span>
        {isPending1 ? <WaveDots active /> : <Icon iconName="edit" />}
      </IconButton>
      <IconButton
        data-testid={`${datasourceUid}-more`}
        className={className}
        color="primary"
        aria-label="display more actions"
        onClick={handleClick}
        onMouseDown={stopPropagation}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          More Actions
        </span>
        <Icon iconName="ellipsis-v" />
        <IconPulse
          pulseKey="ds_more"
          pulseMessage="Click this icon to view more actions, that you can perform on this data source."
        />
      </IconButton>
      {anchorEl && open ? (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          open={open}
          onClose={handleClose}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
        >
          <MenuItem
            data-testid={`${datasourceUid}-sync`}
            disabled={
              store.getRecord(value)?.preventResync === 'Y' ||
              store.getRecord(value)?.typeValue?.toLowerCase() === 'dummy' ||
              isViewOnlyUser
            }
            onClick={async (e) => {
              e.stopPropagation();
              setAnchorEl(undefined);
              const row = store.getCurrentRecord();
              await syncStore.clear();
              await syncStore.createNew({
                partialRecord: { ds: row?.datasourceUid },
                rs: 'I',
              });
              const success = await syncStore.save({
                silent: true,
                feedback: 'NONE',
              });
              if (success) {
                const r = syncStore.getCurrentRecord();
                const changes = r?.changes;
                if (changes && changes.length > 0) {
                  platform.showSuccess(
                    `Table sync'ed successfully! ${changes.join(', ')}.`,
                  );
                } else {
                  platform.showInfo('Table is already in sync!');
                }
              }
              await syncStore.clear();
              await dsAttrStore.clear();
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              <Icon iconName="redo" />
            </ListItemIcon>
            Sync
            <MenuItemPulse
              pulseKey="ds_sync"
              pulseMessage="Check this option to syncronize this datasource with the database table. If the table has been altered after creating this datasource, performing a sync will make the neccessary changes to the datasource metadata."
            />
          </MenuItem>
          <MenuItem
            data-testid={`${datasourceUid}-access`}
            onClick={(e) => {
              e.stopPropagation();
              platform.startAsyncTransition(async () => {
                store.setCurrentRecordId(value);
                const record = store.getRecord(value);
                if (record.datasourceUid) {
                  setEditId(record.datasourceUid);
                  setAnchorEl(undefined);
                  setView('access');
                }
              }, setPending2);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending2 ? (
                <WaveDots active />
              ) : (
                <Icon iconName="shield-alt" />
              )}
            </ListItemIcon>
            Access Settings
          </MenuItem>
          <MenuItem
            data-testid={`${datasourceUid}-ref`}
            onClick={(e) => {
              e.stopPropagation();
              setShowPopup(true);
            }}
            disabled={isBusy || refObjSize === 0}
          >
            <ListItemIcon className={classes.listIcon}>
              <Icon iconName="browser" />
            </ListItemIcon>
            Referred In
            {isBusy ? (
              <WaveDots active />
            ) : (
              <Chip sx={{ ml: 2 }} label={refObjSize} size="small" />
            )}
          </MenuItem>
          <MenuItem
            data-testid={`${datasourceUid}-duplicate`}
            disabled={isViewOnlyUser}
            onClick={async (e) => {
              e.stopPropagation();
              platform.startAsyncTransition(async () => {
                store.setCurrentRecordId(value);
                const record = store.getRecord(value);
                const p1 = dsEditStore
                  .query({
                    props: {
                      filter: [{ datasourceUid: { is: record.datasourceUid } }],
                      limit: 1,
                    },
                  })
                  .then(async () => {
                    const row = dsEditStore.getRecord(value);
                    await dsEditStore.clear();
                    await dsEditStore.createNew({
                      partialRecord: {
                        ...toNewRow(row),
                        copy_from: record.datasourceUid,
                        datasourceUid: `CopyOf${record.datasourceUid}`,
                      },
                      rs: 'I',
                    });
                  });
                const p2 = dsAttrStore.query({
                  props: {
                    filter: [{ datasourceUid: { is: record.datasourceUid } }],
                    limit: 1000,
                  },
                });
                await Promise.all([p1, p2]);
                setAnchorEl(undefined);
                await store.createNew({
                  partialRecord: { copy_from: value },
                  rs: 'I',
                });
                set(maximizeSidebarWidthAtom, 'Y');
                setEditId('');
                setView('edit');
              }, setPending3);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending3 ? <WaveDots active /> : <Icon iconName="copy" />}
            </ListItemIcon>
            Duplicate
          </MenuItem>
          <MenuItem
            data-testid={`${datasourceUid}-createpage`}
            disabled={isViewOnlyUser}
            onClick={async (e) => {
              e.stopPropagation();
              platform.startAsyncTransition(async () => {
                store.setCurrentRecordId(value);
                const record = store.getRecord(value);
                const ds = record.datasourceUid;
                if (ds) {
                  set(sidebarDatasourceEditModeAtom, 'Edit');
                  set(sidebarPageWizardStepAtom, 'template');
                  set(sidebarPageWizardDataSourceAtom, ds);
                  set(sidebarPageViewAtom, 'wizard');
                  set(activeSidebarPanelAtom, 'pages');
                  set(sidebarPageWizardTitleAtom, 'Choose a Page Template');
                }
              }, setPending3);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              <Icon iconName="desktop" />
            </ListItemIcon>
            Create Page
          </MenuItem>
          <Divider />
          <Box
            data-tip={
              refObjSize > 0
                ? `You must unassign ${datasourceUid} from ${refObjSize} pages and all roles before deleting it!`
                : `Delete DataSource ${datasourceUid}`
            }
            data-tip-delay={refObjSize > 0 ? 0 : undefined}
          >
            <MenuItem
              data-testid={`${datasourceUid}-delete`}
              disabled={isBusy || refObjSize > 0 || isViewOnlyUser}
              onClick={async (e) => {
                e.stopPropagation();
                setAnchorEl(undefined);
                const row = store.getCurrentRecord();
                if (row) {
                  platform.confirmDialog({
                    title: 'Delete DataSource',
                    content: `Are you sure you want to delete this DataSource ${row.datasourceUid}?`,
                    onOk: async () => {
                      await store.deleteRecord(value);
                      const deleted = await store.save({
                        feedback: `DataSource '${row.datasourceUid}' deleted successfully!`,
                      });
                      if (!deleted) {
                        store.reset();
                      } else {
                        await dsAccessStore.clear();
                        await dsAttrStore.clear();
                      }
                    },
                  });
                }
              }}
            >
              <ListItemIcon className={classes.listIcon}>
                <Icon
                  iconName="trash"
                  color="error"
                  iconType="light"
                  width={16}
                  height={16}
                />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Box>
        </Menu>
      ) : null}
      {showPopup && (
        <RefPagesPopup
          appUid={appUid}
          datasourceUid={datasourceUid}
          platform={platform}
          recordsOrder={recordsOrder}
          records={refObjRecs}
          onClose={onClose}
        />
      )}
    </>
  );
}

export default memo(DataSourceActions);
